import { useState } from "react";
import { ArrowRight, Loader2, Upload } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Chapter, Reveal } from "./Shared";
import { openRazorpayCheckout } from "../../lib/razorpay";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NICHES = ["Teacher", "Mama", "Football", "Halloween", "Christmas", "Pet", "Christian", "Retro", "Other"];
const PRODUCTS = ["T-shirt", "Sweatshirt", "Mug", "Tote", "Digital Download"];

const inputCls =
  "w-full rounded-xl border border-white/10 bg-[#070B14]/80 px-4 py-3.5 text-sm text-slate-100 placeholder:text-slate-600 focus:border-amber-400/60 focus:outline-none focus:ring-2 focus:ring-amber-400/20";

export default function DesignRequestBox() {
  const [form, setForm] = useState({ request: "", niche: "", product: "" });
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.request.trim() || sending) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("request", form.request.trim());
      fd.append("niche", form.niche);
      fd.append("product", form.product);
      if (file) fd.append("ref_file", file);
      const { data: order } = await axios.post(`${API}/design-request`, fd);
      if (!order?.order_id) throw new Error("no order");
      setSending(false);
      await openRazorpayCheckout({
        order,
        email: "",
        description: "$1 Launch Trial Design",
        onSuccess: async (res) => {
          try {
            const { data: v } = await axios.post(`${API}/billing/verify`, {
              razorpay_order_id: res.razorpay_order_id,
              razorpay_payment_id: res.razorpay_payment_id,
              razorpay_signature: res.razorpay_signature,
            });
            if (!v?.ok) throw new Error("verify failed");
            window.location.assign(
              `${window.location.origin}/welcome?plan=${v.plan_key}&credits=${v.credits}&activated=1`
            );
          } catch (err) {
            toast.error("Payment received but activation failed — contact support and we'll sort it immediately.");
          }
        },
        onDismiss: () => {},
      });
    } catch (err) {
      setSending(false);
      toast.error(err?.response?.data?.detail || "Could not start your checkout — please try again.");
    }
  };

  return (
    <section id="design-request" data-testid="design-request-section" className="relative">
      <div className="mx-auto max-w-3xl px-6 py-16 sm:py-20">
        <Reveal className="text-center">
          <Chapter num="03" label="Start Now" className="justify-center" />
          <h2 className="mt-6 text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
            What do you want us to design?
          </h2>
        </Reveal>

        <Reveal delay={0.1}>
          <form
            onSubmit={submit}
            noValidate
            data-testid="design-request-form"
            className="glass mt-10 space-y-5 rounded-3xl p-7 sm:p-10"
          >
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">
                Your Brief
              </label>
              <textarea
                data-testid="design-request-input"
                rows={4}
                value={form.request}
                onChange={set("request")}
                placeholder="I want a funny Christmas teacher design with retro typography..."
                className="w-full resize-none rounded-xl border border-white/10 bg-[#070B14]/80 px-4 py-3.5 text-sm leading-relaxed text-slate-100 placeholder:text-slate-600 focus:border-amber-400/60 focus:outline-none focus:ring-2 focus:ring-amber-400/20"
              />
            </div>

            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Niche
                </label>
                <select data-testid="design-request-niche" value={form.niche} onChange={set("niche")} className={inputCls}>
                  <option value="">Pick a niche...</option>
                  {NICHES.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Product
                </label>
                <select data-testid="design-request-product" value={form.product} onChange={set("product")} className={inputCls}>
                  <option value="">Pick a product...</option>
                  {PRODUCTS.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">
                Inspiration File (optional)
              </label>
              <label
                htmlFor="design-request-file"
                data-testid="design-request-file-label"
                className="flex cursor-pointer items-center gap-3 rounded-xl border border-dashed border-white/15 bg-[#070B14]/60 px-4 py-4 text-sm text-slate-400 transition-colors hover:border-amber-400/40 hover:text-slate-200"
              >
                <Upload className="h-4 w-4 shrink-0 text-amber-300" />
                {file ? file.name : "Attach a reference image or sketch"}
              </label>
              <input
                id="design-request-file"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
            </div>

            <button
              data-testid="design-request-submit"
              type="submit"
              disabled={sending || !form.request.trim()}
              className="inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-amber-400 px-8 py-4 text-base font-bold text-[#1A1204] transition-all duration-300 hover:bg-amber-300 hover:shadow-[0_0_40px_rgba(245,158,11,0.5)] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
              {sending ? "Opening checkout..." : "Start My $1 Design"}
            </button>
            <p className="text-center text-xs text-slate-600">
              One design, one dollar. Paid securely through our verified global merchant gateway.
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
}